import { Component, createRef } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import CardInspector from './CardInspector';
import { TagEditor } from './DeckTagMenu';
import DeckCardTile, { getImageUrl } from './DeckCardTile';
import CardGrid from './CardGrid';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { GAME_MODE_OPTIONS } from './DeckEditor';
import { playUI, UI } from '../utils/arena/uiSounds';
import { isPhoneLayout } from '../utils/platform';
import { resolveLocalImageUrl } from '../utils/localApi';
import { buildVariantOwnedMap, variantKey } from '../utils/arena/collectionUtils';
import { artGroupsForCard, printingsInArtGroup, artGroupDisplayName } from '../utils/sorcery/artGroups';
import { t, k } from '../localization/t';
import {
  GOLD, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  GOLD_BTN, BEVELED_BTN, DIALOG_STYLE, FourCorners,
} from '../lib/medievalTheme';

// Sampled once at module load. An OS-level setting; Electron restarts on change.
const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const EASE = [0.22, 1, 0.36, 1];

// One recessed field family for every control in the dialog (text
// inputs, the tag editor, the avatar search and grid well): same fill,
// same border, same radius.
const FIELD = {
  borderRadius: 'var(--radius-md)',
  background: '#080604',
  border: `1px solid ${GOLD} 0.22)`,
  color: '#f4e4b8',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
};

// Quiet neutral captions; the gold heading stays the only loud thing.
const CAPTION_CLASS = 'block text-[0.5cqw] font-semibold mb-1.5';
const CAPTION_STYLE = { color: 'rgba(166, 160, 155, 0.6)', letterSpacing: '0.04em' };

function chipProps(active, extraClassName) {
  const className = extraClassName ? `filter-chip ${extraClassName}` : 'filter-chip';
  return { className, 'data-active': active ? 'true' : 'false' };
}

const VISIBILITY_OPTIONS = [
  { value: 'private', label: k('Private'), description: k('Only visible to you.') },
  { value: 'public', label: k('Public'), description: k('Published to the Deck Library on first save.') },
];

/**
 * Pre-configuration dialog for a fresh deck. The avatar picker leads
 * (every deck needs one, so it's the first decision), followed by
 * name + tags, format and visibility, plus an optional bulk-add list.
 * Hands the whole config to `onCreate`; the deck itself is only
 * persisted on the editor's first save, same as before.
 */
export default class CreateDeckModal extends Component {
  state = {
    name: '',
    tags: [],
    // Selected avatar variant: { cardId, printingId, foiling, artGroup,
    // label, imageUrl } or null. The concrete printing is the only
    // channel by which the chosen art/foil reaches the deck.
    avatar: null,
    avatarQuery: '',
    // Unowned variant clicked in a ranked format; drives the inline
    // "switch to sandbox?" prompt. (The app-level askConfirm dialog
    // renders at z-150, under this modal, so it can't be used here.)
    pendingUnownedVariant: null,
    gameMode: 'constructed',
    visibility: 'private',
    // Space-on-hover card inspector: { card, printing } or null.
    inspected: null,
    // Scroll-edge affordance for the avatar grid: an edge shadow shows
    // whenever there's content past that edge.
    gridAtTop: true,
    gridAtBottom: false,
  };

  gridRef = createRef();

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    // The grid measures itself over the first frames; check the edges
    // once it has.
    this._shadowTimer = setTimeout(this.updateGridShadows, 150);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.avatarQuery !== this.state.avatarQuery) {
      clearTimeout(this._shadowTimer);
      this._shadowTimer = setTimeout(this.updateGridShadows, 60);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    clearTimeout(this._shadowTimer);
  }

  isEditableTarget = (el) => {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  };

  // Space toggles the card inspector for the hovered avatar, same as
  // the deck builder's collection grid.
  handleKeyDown = (e) => {
    if (this.isEditableTarget(e.target)) return;
    if (e.key !== ' ' && e.code !== 'Space') return;
    e.preventDefault();
    // OS key-repeat fires while Space is held; only act on the first.
    if (e.repeat) return;
    if (this.state.inspected) {
      this.setState({ inspected: null });
    } else if (this._hoveredVariant) {
      playUI(UI.INSPECTOR_OPEN);
      this.setState({ inspected: { card: this._hoveredVariant.card, printing: this._hoveredVariant.printing } });
    }
  };

  updateGridShadows = () => {
    const el = this.gridRef.current?.scrollRef?.current;
    if (!el) return;
    const gridAtTop = el.scrollTop <= 1;
    const gridAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    if (gridAtTop !== this.state.gridAtTop || gridAtBottom !== this.state.gridAtBottom) {
      this.setState({ gridAtTop, gridAtBottom });
    }
  };

  // Variant ownership, cached by collection reference. The map covers
  // the whole collection, so don't rebuild it per keystroke.
  getVariantOwnedMap() {
    const collection = this.props.profile?.collection;
    if (this._ownedInputCollection === collection && this._ownedCache) return this._ownedCache;
    this._ownedInputCollection = collection;
    this._ownedCache = buildVariantOwnedMap(collection || []);
    return this._ownedCache;
  }

  // One tile per (avatar card x art group x foiling), standard finish
  // first. Same expansion as the deck-builder collection grid in
  // 'all' scope: every variant that exists is listed, owned or not.
  getAvatarVariants() {
    const cards = this.props.sorceryCards;
    if (!Array.isArray(cards)) return [];
    const variantOwned = this.getVariantOwnedMap();
    const avatars = cards.filter((c) => c.type === 'Avatar').sort((a, b) => a.name.localeCompare(b.name));
    const entries = [];
    for (const card of avatars) {
      const owned = variantOwned.get(card.unique_id) || new Map();
      for (const artGroup of artGroupsForCard(card)) {
        const printings = printingsInArtGroup(card, artGroup);
        if (printings.length === 0) continue;
        const foilings = new Set(printings.map((p) => p.foiling || 'S'));
        for (const foiling of ['S', ...[...foilings].filter((f) => f !== 'S')]) {
          if (!foilings.has(foiling)) continue;
          const printing = printings.find((p) => (p.foiling || 'S') === foiling);
          if (!printing) continue;
          entries.push({
            card, printing, artGroup, foiling,
            ownedQty: owned.get(variantKey(artGroup, foiling)) || 0,
          });
        }
      }
    }
    return entries;
  }

  getShownVariants() {
    const q = this.state.avatarQuery.trim().toLowerCase();
    const variants = this.getAvatarVariants();
    return q
      ? variants.filter((v) => v.card.name.toLowerCase().includes(q) || this.variantLabel(v).toLowerCase().includes(q))
      : variants;
  }

  getAllTags() {
    const tags = new Set();
    for (const d of this.props.savedDecks || []) {
      for (const t of d.tags || []) tags.add(t);
    }
    return [...tags].sort((a, b) => a.localeCompare(b));
  }

  variantLabel(v) {
    const base = artGroupDisplayName(v.card, v.artGroup) || v.card.name;
    if (v.foiling === 'R') return t('{name} (Rainbow Foil)', { name: base });
    if (v.foiling === 'F') return t('{name} (Foil)', { name: base });
    return base;
  }

  selectVariant = (v) => {
    playUI(UI.EQUIP);
    this.setState({
      avatar: {
        cardId: v.card.unique_id,
        printingId: v.printing.unique_id,
        foiling: v.foiling,
        artGroup: v.artGroup,
        label: this.variantLabel(v),
        imageUrl: v.printing.image_url ? resolveLocalImageUrl(v.printing.image_url) : null,
      },
      pendingUnownedVariant: null,
    });
  };

  handlePickVariant = (v) => {
    const locked = v.ownedQty === 0 && this.state.gameMode !== 'sandbox';
    if (locked) {
      this.setState({ pendingUnownedVariant: v });
      return;
    }
    this.selectVariant(v);
  };

  handleAddTag = (tag) => {
    const { tags } = this.state;
    if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) return;
    this.setState({ tags: [...tags, tag] });
  };

  handleRemoveTag = (tag) => {
    this.setState({ tags: this.state.tags.filter((t) => t !== tag) });
  };

  handleCreate = () => {
    const { avatar } = this.state;
    this.props.onCreate({
      name: this.state.name.trim() || 'Untitled Deck',
      tags: this.state.tags,
      gameMode: this.state.gameMode,
      visibility: this.state.visibility,
      avatar: avatar ? { cardId: avatar.cardId, printingId: avatar.printingId } : null,
    });
  };

  // CardGrid plumbing, mirroring the deck builder's collection grid so
  // avatars appear and behave identically: foil variants render in the
  // grid's shared WebGL foil scene at rest (imageHidden DOM tile), and
  // unowned variants get the builder's exact cell-level dim.
  getItemKey = (v) => `${v.card.unique_id}-${v.artGroup}-${v.foiling}`;

  getItemFoil = (v) => {
    if (!isFoilFinish(v.foiling)) return null;
    return { imageUrl: getImageUrl({ card: v.card, printing: v.printing }) || '', zones: foilZonesForCard(v.card) };
  };

  getItemCellStyle = (v) => {
    const locked = v.ownedQty === 0 && this.state.gameMode !== 'sandbox';
    return locked ? { opacity: 0.55, filter: 'grayscale(0.5) brightness(0.8)' } : {};
  };

  renderTile = (v, { imageHidden, onTilt }) => {
    const { avatar, gameMode } = this.state;
    const isSelected = avatar
      && avatar.cardId === v.card.unique_id
      && avatar.artGroup === v.artGroup
      && avatar.foiling === v.foiling;
    const locked = v.ownedQty === 0 && gameMode !== 'sandbox';
    const name = this.variantLabel(v);

    return (
      <div className="relative" title={locked ? t('{name}. Not owned; pick it to build in Sandbox.', { name }) : name}>
        <DeckCardTile
          entry={{ card: v.card, printing: v.printing, zone: 'avatar', entryIndex: 0 }}
          isSelected={isSelected}
          imageHidden={imageHidden}
          onTilt={onTilt}
          onClick={() => this.handlePickVariant(v)}
          onHoverChange={(hovered) => {
            if (hovered) this._hoveredVariant = v;
            else if (this._hoveredVariant === v) this._hoveredVariant = null;
          }}
        />
        {/* Unmissable selected state: gold ring + glow over the tile
            (z-lifted above the shared foil canvas) and a check badge
            on the corner. */}
        {isSelected ? (
          <>
            <div
              className="absolute inset-0 pointer-events-none rounded-xl"
              style={{ zIndex: 5, boxShadow: `inset 0 0 0 2px ${ACCENT_GOLD}, 0 0 18px rgba(212,168,67,0.45)` }}
            />
            <div
              className="absolute pointer-events-none flex items-center justify-center rounded-full"
              style={{
                zIndex: 5,
                top: '-0.4cqw',
                right: '-0.4cqw',
                width: '1.4cqw',
                height: '1.4cqw',
                background: ACCENT_GOLD,
                border: '1px solid #1a1408',
                boxShadow: '0 2px 6px rgba(0,0,0,0.6), 0 0 10px rgba(212,168,67,0.5)',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="#1a1408" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  // Leads the dialog: caption + current pick on one line, search, then
  // the always-open card grid in a recessed well.
  renderAvatarSection(phone) {
    const { avatar, avatarQuery } = this.state;
    const shown = this.getShownVariants();

    return (
      <div className={phone ? 'stagger-in' : 'stagger-in flex flex-col flex-1 min-h-0'} style={{ '--stagger': '80ms' }}>
        <div className="flex items-end justify-between gap-3 shrink-0">
          <div className={CAPTION_CLASS} style={CAPTION_STYLE}>{t('Avatar')}</div>
          {avatar ? (
            <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
              <span className="text-[0.5625cqw] truncate" style={{ color: ACCENT_GOLD }}>{avatar.label}</span>
              <button
                type="button"
                aria-label={t('Clear avatar')}
                title={t('Clear avatar')}
                className="shrink-0 inline-flex items-center justify-center cursor-pointer transition-[background-color,color,scale] duration-100 active:scale-[0.96] hover:!bg-[rgba(180,60,60,0.35)] hover:!text-white"
                style={{ width: '1.1cqw', height: '1.1cqw', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'rgba(215,200,150,0.7)' }}
                onClick={() => this.setState({ avatar: null })}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: '0.55cqw', height: '0.55cqw' }}>
                  <path d="m6 6 12 12" /><path d="m18 6-12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <span className="text-[0.5625cqw] mb-1.5" style={{ color: TEXT_MUTED }}>{t('Pick who leads this deck.')}</span>
          )}
        </div>
        <div className="flex items-center mb-2 shrink-0" style={{ ...FIELD, height: '2cqw' }}>
          <div className="pl-2.5 pr-2 shrink-0" style={{ color: `${GOLD} 0.5)` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.7cqw', height: '0.7cqw' }}>
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={t('Search avatars…')}
            value={avatarQuery}
            onInput={(e) => this.setState({ avatarQuery: e.target.value })}
            className="flex-1 min-w-0 pr-2.5 h-full text-xs outline-none bg-transparent"
            style={{ color: TEXT_BODY }}
          />
        </div>
        {/* The grid sits directly on the dialog surface - no framed
            well, the cards ARE the section. It takes every row of
            height the dialog has to spare. CardGrid is the deck
            builder's grid: virtualized, shared foil scene, native
            scroll. */}
        <div
          className={phone ? 'relative flex flex-col' : 'relative flex-1 min-h-0 flex flex-col'}
          style={phone ? { height: '14rem' } : undefined}
        >
          <CardGrid
            ref={this.gridRef}
            items={shown}
            gridSize={phone ? 3 : 4}
            scrollPadClassName="px-2"
            refreshKey={avatarQuery}
            getItemKey={this.getItemKey}
            getItemFoil={this.getItemFoil}
            getItemCellStyle={this.getItemCellStyle}
            renderTile={this.renderTile}
            onViewChange={this.updateGridShadows}
          />
          {/* Scroll-edge shadows: fade in whenever content continues
              past that edge, so the grid reads as scrollable. Above
              the shared foil canvas (z 1). */}
          <div
            className="absolute left-0 right-0 top-0 pointer-events-none transition-opacity duration-150"
            style={{ height: '1.5cqw', zIndex: 6, opacity: this.state.gridAtTop ? 0 : 1, background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), rgba(0,0,0,0))' }}
          />
          <div
            className="absolute left-0 right-0 bottom-0 pointer-events-none transition-opacity duration-150"
            style={{ height: '1.5cqw', zIndex: 6, opacity: this.state.gridAtBottom ? 0 : 1, background: 'linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))' }}
          />
          {shown.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-xs italic" style={{ color: TEXT_MUTED }}>
              {t('No avatars match.')}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderSandboxPrompt() {
    const v = this.state.pendingUnownedVariant;
    return (
      <AnimatePresence>
        {v ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.16, ease: EASE }}
            className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit]"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(2px)' }}
          >
            <motion.div
              initial={REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
              animate={REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.18, ease: EASE }}
              className="px-5 py-4 text-center"
              style={{ maxWidth: '80%' }}
            >
              <div className="text-base font-semibold arena-heading" style={{ color: '#f4e4b8', textShadow: '0 0 12px rgba(180,140,60,0.18)' }}>{t('Switch to Sandbox?')}</div>
              <p className="text-xs mt-2" style={{ color: TEXT_BODY, lineHeight: 1.5 }}>
                {t("You don't own {name}. Sandbox decks can use any card, but they aren't ranked-eligible.", { name: this.variantLabel(v) })}
              </p>
              <div className="flex justify-center gap-2 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 text-xs font-medium transition-[transform,filter] duration-100 active:scale-[0.97]"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  data-sound={UI.CANCEL}
                  onClick={() => this.setState({ pendingUnownedVariant: null })}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="px-5 py-2 text-xs font-semibold transition-[transform,filter] duration-100 active:scale-[0.97]"
                  style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)' }}
                  data-sound={UI.CONFIRM}
                  onClick={() => {
                    this.setState({ gameMode: 'sandbox' });
                    this.selectVariant(v);
                  }}
                >
                  {t('Switch to Sandbox')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  }

  render() {
    const { onClose } = this.props;
    const { error, showBulk, gameMode, visibility } = this.state;
    const phone = isPhoneLayout();
    const caption = (label) => <div className={CAPTION_CLASS} style={CAPTION_STYLE}>{label}</div>;
    const modeDescription = t(GAME_MODE_OPTIONS.find((o) => o.value === gameMode)?.description || '');
    const visibilityDescription = t(VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.description || '');

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.16, ease: EASE }}
        className="fixed inset-0 z-[300] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      >
        <motion.div
          initial={REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, scale: 0.965, y: 8 }}
          animate={REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.2, ease: EASE }}
          className="relative flex flex-col"
          style={{
            ...DIALOG_STYLE,
            isolation: 'isolate',
            width: phone ? '22rem' : '44cqw',
            // Fixed height on desktop so the avatar grid can flex into
            // all the room the form doesn't need.
            ...(phone ? { maxHeight: '92cqh' } : { height: 'min(52cqw, 92cqh)' }),
          }}
        >
          <FourCorners />

          <div className="stagger-in flex items-start justify-between px-5 pt-5 pb-4 shrink-0" style={{ '--stagger': '40ms' }}>
            <div>
              <h2 className="text-lg font-semibold arena-heading" style={{ color: '#f4e4b8', textShadow: '0 0 12px rgba(180,140,60,0.18)' }}>{t('Create Deck')}</h2>
              <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>{t('Set up the basics. Everything can be changed later.')}</p>
            </div>
            <button
              type="button"
              aria-label={t('Close')}
              className="inline-flex items-center justify-center cursor-pointer transition-[background-color,color,scale] duration-100 active:scale-[0.96] hover:!bg-[rgba(180,60,60,0.35)] hover:!text-white"
              style={{ width: '2cqw', height: '2cqw', borderRadius: 'var(--radius-md)', background: 'transparent', color: 'rgba(215,200,150,0.7)' }}
              data-sound={UI.CANCEL}
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                <path d="m6 6 12 12" /><path d="m18 6-12 12" />
              </svg>
            </button>
          </div>

          <div className={phone
            ? 'px-5 pb-5 flex flex-col gap-4 min-h-0 overflow-y-auto medieval-scrollbar'
            : 'px-5 pb-5 flex flex-col gap-4 min-h-0 flex-1'}
          >
            {this.renderAvatarSection(phone)}

            {/* Name and tags share one row: both are short identity
                fields, and pairing them keeps the form compact under
                the avatar grid. */}
            <div className="stagger-in grid gap-x-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '--stagger': '120ms' }}>
              <div>
                {caption(t('Name'))}
                <input
                  type="text"
                  placeholder={t('Name your deck…')}
                  maxLength={100}
                  value={this.state.name}
                  onInput={(e) => this.setState({ name: e.target.value, error: null })}
                  className="w-full px-3 text-xs outline-none transition-[border-color,box-shadow] duration-150 focus:!border-[rgba(212,168,67,0.42)]"
                  style={{ ...FIELD, height: '2.25cqw' }}
                />
              </div>
              <div>
                {caption(t('Tags'))}
                <TagEditor
                  tags={this.state.tags}
                  allTags={this.getAllTags()}
                  onAdd={this.handleAddTag}
                  onRemove={this.handleRemoveTag}
                  fieldStyle={{ ...FIELD, minHeight: '2.25cqw' }}
                />
              </div>
            </div>

            {/* Format and Visibility as single-select chip rows (the
                same control the advanced-filters panels use), with the
                selected option's description as living microcopy - one
                control language for the whole dialog. */}
            <div className="stagger-in grid gap-x-4" style={{ gridTemplateColumns: '3fr 2fr', '--stagger': '150ms' }}>
              <div>
                {caption(t('Format'))}
                <div className="flex flex-wrap items-center gap-1">
                  {GAME_MODE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      {...chipProps(gameMode === opt.value)}
                      onClick={() => this.setState({ gameMode: opt.value })}
                    >
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                {caption(t('Visibility'))}
                <div className="flex flex-wrap items-center gap-1">
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      {...chipProps(visibility === opt.value)}
                      onClick={() => this.setState({ visibility: opt.value })}
                    >
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-[0.5625cqw] -mt-2.5" style={{ color: TEXT_MUTED }}>
              {modeDescription} {visibilityDescription}
            </div>

            <div className="stagger-in flex justify-end gap-2 mt-auto" style={{ '--stagger': '180ms' }}>
              <button
                type="button"
                className="px-4 py-2 text-xs font-medium transition-[transform,filter] duration-100 active:scale-[0.97]"
                style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                data-sound={UI.CANCEL}
                onClick={onClose}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="px-5 py-2 text-xs font-semibold transition-[transform,filter] duration-100 active:scale-[0.97]"
                style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)' }}
                data-sound={UI.CONFIRM}
                onClick={this.handleCreate}
              >
                {t('Create')}
              </button>
            </div>
          </div>

          {this.renderSandboxPrompt()}
        </motion.div>

        {this.state.inspected ? (
          <CardInspector
            card={this.state.inspected.card}
            imageUrl={this.state.inspected.printing?.image_url}
            rarity={this.state.inspected.card?.rarity}
            foiling={this.state.inspected.printing?.foiling}
            onClose={() => this.setState({ inspected: null })}
          />
        ) : null}
      </motion.div>
    );
  }
}
